import { CubeQuery } from '../../../types/cube';
import { ChannelGrouping, IndicationLevel } from '../../../types/charts';

export const HCPKeys = {
  all: ['hcp'] as const,
  aggregated: ['aggregated'] as const,
  list: (query: CubeQuery) => [...HCPKeys.all, 'list', query] as const,
  channelInteraction: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'channel-interaction', hcpBk, query] as const,
  totalCount: (query: CubeQuery) => [...HCPKeys.all, 'total-hcp-count', query] as const,
  insight: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'insight', hcpBk, query] as const,
  segmentation: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'segmentation-value-motivation-and-engagement', hcpBk, query] as const,
  ecosystemRole: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'segmentation-ecosystem-role', hcpBk, query] as const,
  profilingQuestions: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'segmentation-profiling-questions', hcpBk, query] as const,
  getNumberEvents: (query: CubeQuery) => [...HCPKeys.all, 'get-event-number', query] as const,
  numberHcpsContacted: (query: CubeQuery) => [...HCPKeys.all, 'number-hcps-contacted', query] as const,
  emailsBouncedCount: (query: CubeQuery) => [...HCPKeys.all, 'email-bounced-count', query] as const,
  emailsClickedCount: (query: CubeQuery) => [...HCPKeys.all, 'email-clicked-count', query] as const,
  emailsSentCount: (query: CubeQuery) => [...HCPKeys.all, 'email-sent-count', query] as const,
  totalAccountsRegistered: (query: CubeQuery) => [...HCPKeys.all, 'total-accounts-registered', query] as const,
  interactionsGroupedByHCP: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'interactions-grouped-by-HCP-current', query] as const,
  eventDetails: (query: CubeQuery) => [...HCPKeys.aggregated, 'event-details', query] as const,
  allInteractionsGroupedByHcp: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'all-interactions-grouped-by-hcp', query] as const,
  allInteractionsGroupedByHcpForHco: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'all-interactions-grouped-by-hcp-for-hco', query] as const,
  totalHCPRegistered: (query: CubeQuery) => [...HCPKeys.all, 'total-hcp-registered', query] as const,
  userAcquisitions: (query: CubeQuery) => [...HCPKeys.all, 'user-acquisitions', query] as const,
  useGetEventTypesOptions: (query: CubeQuery) => [...HCPKeys.aggregated, 'us-get-event-types-options', query] as const,
  useGetFieldForcesOptions: (query: CubeQuery) => [...HCPKeys.aggregated, 'us-get-field-force-options', query] as const,
  useGetIndicationType: (query: CubeQuery) => [...HCPKeys.aggregated, 'use-get-indication-type', query] as const,
  useGetEmailCampaignOptions: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'us-get-email-campaign-options', query] as const,
  interactionsList: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-interaction-lists', hcpBk, query] as const,
  interactionLegendTypes: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-interaction-legend-types', hcpBk, query] as const,
  latestInteraction: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-latest-interaction', hcpBk, query] as const,
  mostfrequentcollabolators: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-most-frequent-collabolators', hcpBk, query] as const,
  mostFrequentCollaborators: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-most-frequent-collaborators', hcpBk, query] as const,
  mostFrequentCollaboratorsMeasures: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-most-frequent-collaborators-measures', hcpBk, query] as const,
  mostFrequentCollaboratorsTerritories: (hcpBk: string, employeePks: string[], query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-most-frequent-collaborators-territories', hcpBk, employeePks, query] as const,
  interactionGroupedByMonth: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'interaction-grouped-by-month', query] as const,
  interactionTypes: (query: CubeQuery) => [...HCPKeys.aggregated, 'interaction-types', query] as const,
  otherHcpsAndRocheContributors: (query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-interaction-all-otherhcos-rochecontributors', query] as const,
  crossChannelEngagement: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'cross-channel-engagement', hcpBk, query] as const,
  channelRateGroupedByMonth: (query: CubeQuery) =>
    [...HCPKeys.aggregated, 'channel-rate-grouped-by-month', query] as const,
  coverageBySegmentationContacted: (segmentationCase: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-coverage-by-segmentation-contacted', segmentationCase, query] as const,
  coverageBySegmentationTotal: (segmentationCase: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-coverage-by-segmentation-total', segmentationCase, query] as const,
  emailEngagementHeatmap: (query: CubeQuery) => [...HCPKeys.all, 'email-engagement-heatmap', query] as const,
  lineInformationData: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-line-information-data', hcpBk, query] as const,
  HcpXProfileData: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'hcp-x-profile-data', hcpBk, query] as const,
  lineMessagesData: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-engagement-line-messages-data', hcpBk, query] as const,
  useGetConsentTopicOptions: (query: CubeQuery) => [...HCPKeys.all, 'aggregated-consent-filter', query] as const,
  useGetCountryLegalBasisTierOptions: (query: CubeQuery) =>
    [...HCPKeys.all, 'country-legal-basis-tier-filter', query] as const,
  allChannelsInteractionOptions: (query: CubeQuery) =>
    [...HCPKeys.all, 'all-channels-interaction-type-filter', query] as const,
  clinicalTrial: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'hcp-clinical-trial', hcpBk, query] as const,
  emailCampaignInteractions: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-engagement-email-campaign-interactions', hcpBk, query] as const,
  messagingCampaignMetrics: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-engagement-messaging-campaign-metrics', hcpBk, query] as const,
  messagingCampaignInteractions: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-engagement-messaging-campaign-interactions', hcpBk, query] as const,
  portalInteractionDetails: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-portal-interactions', hcpBk, query] as const,
  portalActivity: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'hcp-portal-activity', hcpBk, query] as const,
  websiteInteractionDetails: (interactionId: string, query: CubeQuery) =>
    [...HCPKeys.all, 'website-interaction-details', interactionId, query] as const,
  engagementPerProduct: (hcpBk: string, channelGrouping: ChannelGrouping) =>
    [...HCPKeys.all, 'engagement-per-product', hcpBk, channelGrouping] as const,
  engagementPerIndication: (hcpBk: string, channelGrouping: ChannelGrouping, indicationLevel: IndicationLevel) =>
    [...HCPKeys.all, 'engagement-per-indication', hcpBk, channelGrouping, indicationLevel] as const,
  engagementPerChannel: (hcpBk: string, channelGrouping: ChannelGrouping) =>
    [...HCPKeys.all, 'engagement-per-channel', hcpBk, channelGrouping] as const,
  HcpBlueskyProfileData: (hcpBk: string, query: CubeQuery) => [
    ...HCPKeys.all,
    'hcp-bluesky-profile-data',
    hcpBk,
    query,
  ],
  socialMediaPosts: (query: CubeQuery) => [...HCPKeys.all, 'social-media-posts', query] as const,
  publications: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'publications', hcpBk, query] as const,
  publicationByIds: (pmIds: string[], query: CubeQuery) => [...HCPKeys.all, 'publications', pmIds, query] as const,
  publicationsCount: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'publications-count', hcpBk, query] as const,
  congressAbstracts: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-congress-abstracts', hcpBk, query] as const,
  contractsData: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'hcp-contracts', hcpBk, query] as const,
  ClinicalTrialsKeys: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-clinical-trials', hcpBk, query] as const,
  ClinicalTrialPhaseCount: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-clinical-trial-phase-count', hcpBk, query] as const,
  ClinicalTrialStatusCount: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-clinical-trial-status-count', hcpBk, query] as const,
  clinicalTrialsByStudyNumber: (studyNumbers: string[], query: CubeQuery) =>
    [...HCPKeys.all, 'clinical-trials-by-study-number', studyNumbers, query] as const,
  clinicalTrialCount: (hcpBk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'clinical-trial-count', hcpBk, query] as const,
  latestVoCCustomerMetrics: (query: CubeQuery) => [...HCPKeys.all, 'hcp-latest-vocal-metrics', query] as const,
  interactionVoCMetrics: (query: CubeQuery) => [...HCPKeys.all, 'hcp-interaction-voc-metrics', query] as const,
  interactionVoCDetails: (interactionPk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-interaction-voc-details', interactionPk, query] as const,
  interactionVoCCustomQuestions: (interactionPk: string, query: CubeQuery) =>
    [...HCPKeys.all, 'hcp-interaction-voc-custom-questions', interactionPk, query] as const,
  HCPVoCCustomerQuestion: (query: CubeQuery) => [...HCPKeys.all, 'hcp-voc-custom-question', query] as const,
  useGetPpocOptions: (query: CubeQuery) => [...HCPKeys.all, 'aggregated-consent-ppoc-filter', query] as const,
  enquiries: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'enquiries', hcpBk, query] as const,
  tabActivation: (hcpBk: string, query: CubeQuery) => [...HCPKeys.all, 'tab-activation', hcpBk, query] as const,
  congressAbstractsByEmbaseIds: (embaseIds: string[], query: CubeQuery) =>
    [...HCPKeys.all, 'congress-abstracts-by-embase-ids', embaseIds, query] as const,
};
